import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { teamStore } from '@/stores/TeamStore'
import { agentStore } from '@/stores/AgentStore'
import { apiClient } from '@/services/api'
import {
  ArrowLeftIcon,
  PlayIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  ArrowsRightLeftIcon,
  Squares2X2Icon,
  ChartBarSquareIcon,
  UsersIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { TeamCoordinationStrategy, AgentRole } from '@/types'
import type { TeamExecution } from '@/types'

const TeamDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // Fetch team details
  useEffect(() => {
    if (id) {
      teamStore.fetchTeamById(id)
      agentStore.fetchAgents()
    }
  }, [id])

  // Fetch team executions
  const { data: executionsData } = useQuery({
    queryKey: ['team-executions', id],
    queryFn: () => apiClient.getTeamExecutions(id!),
    enabled: !!id,
  })

  const team = teamStore.selectedTeam

  const handleExecute = () => {
    navigate(`/teams/${id}/execute`)
  }

  const handleEdit = () => {
    navigate(`/teams/${id}/edit`)
  }

  const handleDelete = async () => {
    if (!id || !team) return

    if (window.confirm(`Are you sure you want to delete "${team.name}"?`)) {
      try {
        await teamStore.deleteTeam(id)
        toast.success('Team deleted successfully')
        navigate('/teams')
      } catch (error) {
        toast.error('Failed to delete team')
      }
    }
  }

  const getStrategyIcon = (strategy: TeamCoordinationStrategy) => {
    const icons = {
      [TeamCoordinationStrategy.SEQUENTIAL]: ArrowsRightLeftIcon,
      [TeamCoordinationStrategy.PARALLEL]: Squares2X2Icon,
      [TeamCoordinationStrategy.HIERARCHICAL]: ChartBarSquareIcon,
      [TeamCoordinationStrategy.CONSENSUS]: UsersIcon,
      [TeamCoordinationStrategy.FEEDBACK_LOOP]: ArrowPathIcon,
    }
    return icons[strategy] || ArrowsRightLeftIcon
  }

  const getStrategyLabel = (strategy: TeamCoordinationStrategy) => {
    const labels = {
      [TeamCoordinationStrategy.SEQUENTIAL]: 'Sequential',
      [TeamCoordinationStrategy.PARALLEL]: 'Parallel',
      [TeamCoordinationStrategy.HIERARCHICAL]: 'Hierarchical',
      [TeamCoordinationStrategy.CONSENSUS]: 'Consensus',
      [TeamCoordinationStrategy.FEEDBACK_LOOP]: 'Feedback Loop',
    }
    return labels[strategy] || strategy
  }

  const getStatusBadgeVariant = (status: string) => {
    const variants: Record<string, 'success' | 'error' | 'warning' | 'gray'> = {
      ACTIVE: 'success',
      INACTIVE: 'gray',
      DRAFT: 'warning',
      ARCHIVED: 'gray',
    }
    return variants[status] || 'gray'
  }

  const getExecutionStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />
      case 'FAILED':
        return <XCircleIcon className="w-5 h-5 text-red-500" />
      case 'RUNNING':
        return <ClockIcon className="w-5 h-5 text-blue-500 animate-spin" />
      default:
        return <ClockIcon className="w-5 h-5 text-muted-foreground" />
    }
  }

  const getExecutionStatusBadgeVariant = (status: string) => {
    const variants: Record<string, 'success' | 'error' | 'warning' | 'gray'> = {
      COMPLETED: 'success',
      FAILED: 'error',
      RUNNING: 'warning',
      PENDING: 'gray',
      CANCELLED: 'gray',
    }
    return variants[status] || 'gray'
  }

  const getRoleBadgeVariant = (role: AgentRole) => {
    const variants: Record<AgentRole, 'primary' | 'success' | 'warning' | 'error' | 'gray'> = {
      [AgentRole.COORDINATOR]: 'primary',
      [AgentRole.RESEARCHER]: 'success',
      [AgentRole.ANALYST]: 'warning',
      [AgentRole.WRITER]: 'primary',
      [AgentRole.REVIEWER]: 'warning',
      [AgentRole.VALIDATOR]: 'error',
      [AgentRole.TESTER]: 'success',
      [AgentRole.DEBUGGER]: 'error',
      [AgentRole.OPTIMIZER]: 'success',
      [AgentRole.SPECIALIST]: 'primary',
      [AgentRole.CUSTOM]: 'gray',
    }
    return variants[role]
  }

  const getAgentName = (agentId: string) => {
    const agent = agentStore.agents.find((a) => a.id === agentId)
    return agent?.name || agentId
  }

  const getCoordinatorName = () => {
    if (!team?.coordinator_agent_id) return 'No coordinator'
    return getAgentName(team.coordinator_agent_id)
  }

  if (teamStore.isLoading || !team) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const StrategyIcon = getStrategyIcon(team.strategy)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/teams')}>
            <ArrowLeftIcon className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{team.name}</h1>
              <Badge variant={getStatusBadgeVariant(team.status)}>{team.status}</Badge>
            </div>
            <p className="text-muted-foreground mt-1">{team.description || 'No description'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={handleExecute}
            disabled={team.status !== 'ACTIVE'}
          >
            <PlayIcon className="w-5 h-5 mr-2" />
            Execute
          </Button>
          <Button variant="secondary" onClick={handleEdit}>
            <PencilIcon className="w-5 h-5 mr-2" />
            Edit
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <TrashIcon className="w-5 h-5 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Team Configuration */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Team Configuration</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Coordination Strategy</p>
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-primary-500/15 rounded-lg">
                      <StrategyIcon className="w-5 h-5 text-primary-600" />
                    </div>
                    <span className="text-sm font-medium text-foreground">
                      {getStrategyLabel(team.strategy)}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Coordinator Agent</p>
                  <p className="text-sm font-medium text-foreground">{getCoordinatorName()}</p>
                </div>
              </div>

              {team.feedback_enabled && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowPathIcon className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-600">
                      Feedback Loop Enabled
                    </span>
                  </div>
                  <p className="text-sm text-blue-600">
                    Max Iterations: <span className="font-medium">{team.max_iterations}</span>
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Team Members */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Team Members ({team.members.length})
            </h3>
            {team.members.length > 0 ? (
              <div className="space-y-2">
                {team.members
                  .sort((a, b) => a.execution_order - b.execution_order)
                  .map((member, index) => (
                    <div
                      key={member.id || index}
                      className="flex items-center gap-3 p-3 bg-muted/50 border border-border rounded-lg"
                    >
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="font-mono">{index + 1}</span>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-foreground">
                            {getAgentName(member.agent_id)}
                          </span>
                          <Badge variant={getRoleBadgeVariant(member.role)} size="sm">
                            {member.role}
                          </Badge>
                          {member.is_required && (
                            <Badge variant="error" size="sm">
                              Required
                            </Badge>
                          )}
                        </div>
                        {member.role_description && (
                          <p className="text-sm text-muted-foreground mt-1">{member.role_description}</p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-muted/50 border-2 border-dashed border-border rounded-lg">
                <UserIcon className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No team members yet</p>
              </div>
            )}
          </Card>

          {/* Recent Executions */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Recent Executions</h3>
            {executionsData?.items && executionsData.items.length > 0 ? (
              <div className="space-y-3">
                {executionsData.items.map((execution: TeamExecution) => (
                  <div
                    key={execution.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => navigate(`/teams/executions/${execution.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      {getExecutionStatusIcon(execution.status)}
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Execution #{execution.id.slice(0, 8)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(execution.created_at), {
                            addSuffix: true,
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {execution.iterations_completed !== undefined && (
                        <span className="text-xs text-muted-foreground">
                          {execution.iterations_completed} iteration
                          {execution.iterations_completed !== 1 ? 's' : ''}
                        </span>
                      )}
                      {execution.total_tokens && (
                        <span className="text-xs text-muted-foreground">
                          {execution.total_tokens.toLocaleString()} tokens
                        </span>
                      )}
                      {execution.total_cost && (
                        <span className="text-xs text-muted-foreground">
                          ${execution.total_cost.toFixed(4)}
                        </span>
                      )}
                      <Badge variant={getExecutionStatusBadgeVariant(execution.status)}>
                        {execution.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <PlayIcon className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground text-sm">No executions yet</p>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-3"
                  onClick={handleExecute}
                  disabled={team.status !== 'ACTIVE'}
                >
                  Run First Execution
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Metadata Sidebar */}
        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Metadata</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">ID</p>
                <p className="text-xs font-mono text-foreground">{team.id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="text-sm text-foreground">
                  {formatDistanceToNow(new Date(team.created_at), { addSuffix: true })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Updated</p>
                <p className="text-sm text-foreground">
                  {formatDistanceToNow(new Date(team.updated_at), { addSuffix: true })}
                </p>
              </div>
              {team.workspace_id && (
                <div>
                  <p className="text-sm text-muted-foreground">Workspace</p>
                  <p className="text-xs font-mono text-foreground">{team.workspace_id}</p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Statistics</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Executions</span>
                <span className="text-lg font-semibold text-foreground">
                  {executionsData?.total || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Team Members</span>
                <span className="text-lg font-semibold text-foreground">
                  {team.members.length}
                </span>
              </div>
              {executionsData?.items && executionsData.items.length > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Success Rate</span>
                  <span className="text-lg font-semibold text-green-600">
                    {(
                      (executionsData.items.filter(
                        (e: TeamExecution) => e.status === 'COMPLETED'
                      ).length /
                        executionsData.items.length) *
                      100
                    ).toFixed(0)}
                    %
                  </span>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
})

export default TeamDetailPage
